import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

const OVERLAY_Z = 10000;
const SPOTLIGHT_SHADOW = '0 0 0 9999px rgba(0, 0, 0, 0.58)';
const RING_COLOR = '#1976d2';
const ICON_RING_COLOR = '#1976d2';

const blockBackgroundInteraction = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

function measureElement(el, padding = 8, borderRadius = 8, paddingX, paddingY) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const px = paddingX ?? padding;
  const py = paddingY ?? padding;
  return {
    top: r.top - py,
    left: r.left - px,
    width: r.width + px * 2,
    height: r.height + py * 2,
    borderRadius,
  };
}

function measureTarget(step) {
  const el = step.resolveTarget?.();
  if (!el) return null;
  return measureElement(el, step.padding ?? 8, step.borderRadius ?? 8, step.paddingX, step.paddingY);
}

function measureIconTarget(step) {
  const el = step.resolveIconTarget?.();
  if (!el) return null;
  return measureElement(el, step.iconPadding ?? 3, step.iconBorderRadius ?? 20);
}

function TownViewTutorialSpotlight({ steps, stepIndex, onBack, onNext, onDone }) {
  const step = steps[stepIndex];
  const [hole, setHole] = useState(null);
  const [iconHole, setIconHole] = useState(null);
  const isLastStep = stepIndex >= steps.length - 1;

  const updateHole = useCallback(() => {
    if (!step) {
      setHole(null);
      setIconHole(null);
      return;
    }
    setHole(measureTarget(step));
    setIconHole(measureIconTarget(step));
  }, [step]);

  useLayoutEffect(() => {
    if (!step) return undefined;
    const el = step.resolveTarget?.();
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    updateHole();
    const retry = window.setInterval(updateHole, 150);
    const stopRetry = window.setTimeout(() => window.clearInterval(retry), 2000);
    return () => {
      window.clearInterval(retry);
      window.clearTimeout(stopRetry);
    };
  }, [step, stepIndex, updateHole]);

  useEffect(() => {
    window.addEventListener('resize', updateHole);
    window.addEventListener('scroll', updateHole, true);
    return () => {
      window.removeEventListener('resize', updateHole);
      window.removeEventListener('scroll', updateHole, true);
    };
  }, [updateHole]);

  const tooltipLeft = hole ? Math.max(16, Math.min(hole.left, window.innerWidth - 416)) : undefined;
  const placeAbove = hole ? hole.top + hole.height + 220 > window.innerHeight : false;

  const tooltipSx = hole
    ? placeAbove
      ? {
          position: 'fixed',
          bottom: window.innerHeight - hole.top + 16,
          left: tooltipLeft,
        }
      : {
          position: 'fixed',
          top: hole.top + hole.height + 16,
          left: tooltipLeft,
        }
    : {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };

  if (!step) return null;

  return createPortal(
    <Box
      sx={{ position: 'fixed', inset: 0, zIndex: OVERLAY_Z, pointerEvents: 'auto' }}
      onClick={blockBackgroundInteraction}
      onMouseDown={blockBackgroundInteraction}
      onPointerDown={blockBackgroundInteraction}
      role="presentation"
    >
      {hole ? (
        <Box
          sx={{
            position: 'fixed',
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            borderRadius: `${hole.borderRadius}px`,
            boxShadow: SPOTLIGHT_SHADOW,
            border: `2px solid ${RING_COLOR}`,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      {iconHole ? (
        <Box
          sx={{
            position: 'fixed',
            top: iconHole.top,
            left: iconHole.left,
            width: iconHole.width,
            height: iconHole.height,
            borderRadius: `${iconHole.borderRadius}px`,
            border: `3px solid ${ICON_RING_COLOR}`,
            boxShadow: `0 0 0 2px rgba(255, 255, 255, 0.9)`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      ) : null}
      {!hole ? (
        <Box
          sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0, 0, 0, 0.58)', pointerEvents: 'none' }}
        />
      ) : null}

      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="town-view-tutorial-step-title"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          ...tooltipSx,
          maxWidth: 400,
          width: 'calc(100% - 32px)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: 2.5,
          boxShadow: 24,
          pointerEvents: 'auto',
        }}
      >
        <Typography id="town-view-tutorial-step-title" variant="h6" component="h2" sx={{ mb: 0.5 }}>
          {step.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Step {stepIndex + 1} of {steps.length}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {step.body}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {stepIndex > 0 ? (
            <Button variant="outlined" size="small" onClick={onBack}>
              Back
            </Button>
          ) : null}
          <Button variant="contained" size="small" onClick={isLastStep ? onDone : onNext}>
            {isLastStep ? 'Done' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Box>,
    document.body,
  );
}

export default TownViewTutorialSpotlight;
